import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '../api/products';
import type { Product } from '@coreledger/shared-types';
import { searchHsn } from '@coreledger/shared-types';
import Pagination from '../components/Pagination';
import { confirm } from '../components/ConfirmDialog';
import { useRole } from '../context/AuthContext';

const ADJUST_REASONS = [
  { value: 'physical_count', label: 'Physical Count Correction' },
  { value: 'damaged', label: 'Damaged / Spoiled' },
  { value: 'theft', label: 'Theft / Shortage' },
  { value: 'opening_correction', label: 'Opening Stock Correction' },
  { value: 'returned_to_supplier', label: 'Returned to Supplier' },
  { value: 'free_sample', label: 'Free Sample' },
  { value: 'other', label: 'Other' },
] as const;

const GST_RATES = [0, 0.1, 0.25, 1, 1.5, 3, 5, 6, 7.5, 12, 18, 28];
const empty = (): Partial<Product> => ({ name: '', type: 'product', unit: 'pcs', gstRate: 18, gstCategory: 'taxable' });
const fmt = (n?: number) => n !== undefined ? '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—';

const CSV_IMPORT_COLUMNS: { key: string; required: boolean; note: React.ReactNode }[] = [
  { key: 'name', required: true, note: "The product or service's name." },
  { key: 'type', required: false, note: <><code>product</code> or <code>service</code> — defaults to <code>product</code> if left blank.</> },
  { key: 'category', required: false, note: <><code>raw_material</code> or <code>finished_good</code> — leave blank for uncategorized. Groups the BOM editor's dropdowns and the Stock Movement Report.</> },
  { key: 'hsnSac', required: false, note: 'HSN code (goods) or SAC code (services).' },
  { key: 'unit', required: false, note: <>Defaults to <code>pcs</code>.</> },
  { key: 'rate', required: false, note: <>Selling price. Defaults to <code>0</code>.</> },
  { key: 'gstRate', required: false, note: <>GST %. Defaults to <code>18</code>.</> },
  { key: 'stock', required: false, note: <>Current stock quantity — also seeds this product's opening stock basis for the Stock Movement Report, since a freshly-imported product has no purchase history of its own yet. Defaults to <code>0</code>.</> },
  { key: 'openingStockRate', required: false, note: <>Cost per unit of that opening stock (not the selling price above) — used for FIFO valuation in the Stock Movement Report/Balance Sheet. Leave blank to use the selling price as an estimate until corrected.</> },
  { key: 'reorderLevel', required: false, note: <>Defaults to <code>0</code>.</> },
];
const SAMPLE_PRODUCTS_CSV = [
  'name,type,category,hsnSac,unit,rate,gstRate,stock,openingStockRate,reorderLevel',
  'Steel Bolt 10mm,product,raw_material,7318,pcs,4.50,18,500,3.20,50',
  'Annual Maintenance Contract,service,,9987,hr,1200,18,0,,0',
].join('\n');

function downloadCsv(content: string, filename: string) {
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function ProductsPage() {
  const { canDelete } = useRole();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<null | 'create' | Product>(null);
  const [form, setForm] = useState<Partial<Product>>(empty());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [adjustProduct, setAdjustProduct] = useState<Product | null>(null);
  const [adjustForm, setAdjustForm] = useState({ qtyChange: 0, reason: 'physical_count', notes: '' });
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);

  const [hsnSearch, setHsnSearch] = useState('');
  const [showHsnDD, setShowHsnDD] = useState(false);
  const csvRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [csvInfoModal, setCsvInfoModal] = useState(false);
  const [importResult, setImportResult] = useState<{ ok: number; dup: number; fail: number; dupNames: string[] } | null>(null);
  const hsnResults = hsnSearch.length >= 2 ? searchHsn(hsnSearch).slice(0, 8) : [];

  const editingProductId = modal && modal !== 'create' ? modal.id : undefined;
  const { data: fifoCostResult } = useQuery({
    queryKey: ['fifo-cost', editingProductId],
    queryFn: () => productsApi.getFifoCost(editingProductId!),
    enabled: !!editingProductId && form.type === 'product',
    staleTime: 30_000,
  });
  const [page, setPage] = useState(1);

  const { data: productsData, isLoading } = useQuery({ queryKey: ['products', page], queryFn: () => productsApi.list(undefined, page, 50), staleTime: 30 * 1000 });
  const products = productsData?.data ?? [];
  const productsMeta = productsData?.meta;
  const del = useMutation({ mutationFn: productsApi.delete, onSuccess: () => { qc.invalidateQueries({ queryKey: ['products'] }); qc.refetchQueries({ type: 'all' }); } });

  // Bulk-select — same Set<string>-of-ids pattern already used on
  // InvoicesPage.tsx/LorryReceiptsPage.tsx/BankTransactionsPage.tsx.
  // Selection only ever spans the current (server-paginated) page's own
  // rows, same as those pages' own "select all" checkbox — bulkDelete acts
  // on exactly whatever's selected; deleteAll (below) is the separate,
  // unfiltered "everything" action.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const bulkDel = useMutation({
    mutationFn: productsApi.bulkDelete,
    onSuccess: () => { setSelected(new Set()); qc.invalidateQueries({ queryKey: ['products'] }); qc.refetchQueries({ type: 'all' }); },
  });
  const delAll = useMutation({
    mutationFn: productsApi.deleteAll,
    onSuccess: () => { setSelected(new Set()); setPage(1); qc.invalidateQueries({ queryKey: ['products'] }); qc.refetchQueries({ type: 'all' }); },
  });

  // How many products have no opening-stock basis recorded — see
  // ProductsService.backfillOpeningStock()'s own comment for why this gap
  // exists (Stock Movement Report reads openingStockQty, a different field
  // from the `stock` this page shows) and what filling it does. Only shown
  // when nonzero — most tenants will hit 0 here and never see the button.
  const { data: gapData } = useQuery({ queryKey: ['products-opening-stock-gap'], queryFn: productsApi.openingStockGapCount, staleTime: 30 * 1000 });
  const gapCount = gapData?.count ?? 0;
  const backfill = useMutation({
    mutationFn: productsApi.backfillOpeningStock,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['products-opening-stock-gap'] });
      qc.refetchQueries({ type: 'all' });
    },
  });

  const filtered = products.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.hsnSac ?? '').includes(search));

  function set(k: keyof Product) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const numFields: (keyof Product)[] = ['rate', 'gstRate', 'stock', 'reorderLevel', 'openingStockQty', 'openingStockRate'];
      const v = numFields.includes(k) ? parseFloat(e.target.value) || 0 : e.target.value;
      setForm(f => ({ ...f, [k]: v }));
    };
  }

  async function handleSave() {
    if (!form.name?.trim()) { setError('Name is required'); return; }
    setError(null); setSaving(true);
    try {
      if (modal === 'create') { await productsApi.create(form); }
      else { await productsApi.update((modal as Product).id, form); }
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.refetchQueries({ type: 'all' });
      setModal(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  async function handleAdjustStock() {
    if (!adjustProduct) return;
    if (adjustForm.qtyChange === 0) { setAdjustError('Quantity change cannot be zero'); return; }
    setAdjustSaving(true); setAdjustError(null);
    try {
      await productsApi.adjustStock(adjustProduct.id, {
        date: new Date().toISOString().slice(0, 10),
        qtyChange: adjustForm.qtyChange,
        reason: adjustForm.reason,
        notes: adjustForm.notes || undefined,
      });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.refetchQueries({ type: 'all' });
      setAdjustProduct(null);
    } catch (err) { setAdjustError(err instanceof Error ? err.message : 'Adjustment failed'); }
    finally { setAdjustSaving(false); }
  }

  // category is identity-like (same treatment as type) — openingStockRate is
  // deliberately excluded, same reasoning as Parties' openingBalance
  // exclusion: it's an attribute, not identity, so a corrected opening cost
  // on re-import shouldn't stop a row from matching as a duplicate.
  const TEXT_SIG_FIELDS = ['name', 'type', 'category', 'hsnSac', 'unit'] as const;
  const NUM_SIG_FIELDS = ['rate', 'gstRate', 'stock', 'reorderLevel'] as const;
  type ProductSig = Record<typeof TEXT_SIG_FIELDS[number] | typeof NUM_SIG_FIELDS[number], string>;
  const normText = (v?: string | null) => (v ?? '').trim().toLowerCase();
  // Numeric fields need their own normalizer, not normText — the CSV's raw
  // cell text ("10.00") and a value that's been through parseFloat then
  // back to a stored/created product's own string form ("10") are the same
  // number but different strings, so a plain text compare would silently
  // never match, breaking duplicate detection for every row with a
  // differently-formatted (but equal) number, undetected because it only
  // fails silently.
  const normNum = (v?: string | number | null) => {
    if (v === undefined || v === null || v === '') return '';
    const n = typeof v === 'number' ? v : parseFloat(v);
    return isNaN(n) ? '' : String(n);
  };
  // Same rule as the Parties CSV import: a row counts as a duplicate of an
  // existing product only when every field it *actually provides* matches
  // — a field left blank in the row is never compared. Note this means a
  // re-uploaded row with just a changed price won't be flagged (rate
  // differs), so it imports as a separate row rather than being skipped —
  // consistent with Parties, not a bug.
  function isDuplicateSig(row: Partial<ProductSig>, existing: ProductSig): boolean {
    return [...TEXT_SIG_FIELDS, ...NUM_SIG_FIELDS].every(f => !row[f] || row[f] === existing[f]);
  }

  // Same columns as SAMPLE_PRODUCTS_CSV so an exported file can be re-imported elsewhere unchanged.
  async function handleExportCsv() {
    setExporting(true);
    try {
      const rows: string[] = ['name,type,category,hsnSac,unit,rate,gstRate,stock,openingStockRate,reorderLevel'];
      let page = 1;
      for (;;) {
        const res = await productsApi.list(undefined, page, 200);
        for (const p of res.data) {
          rows.push([p.name, p.type, p.category, p.hsnSac, p.unit, p.rate, p.gstRate, p.stock, p.openingStockRate, p.reorderLevel]
            .map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
        }
        if (page >= (res.meta?.totalPages ?? 1)) break;
        page++;
      }
      downloadCsv(rows.join('\n'), 'products.csv');
    } finally {
      setExporting(false);
    }
  }

  async function fetchAllProductSigs(): Promise<ProductSig[]> {
    const sigs: ProductSig[] = [];
    let page = 1;
    for (;;) {
      const res = await productsApi.list(undefined, page, 200);
      for (const p of res.data) {
        sigs.push({
          name: normText(p.name), type: normText(p.type), category: normText(p.category), hsnSac: normText(p.hsnSac), unit: normText(p.unit),
          rate: normNum(p.rate), gstRate: normNum(p.gstRate), stock: normNum(p.stock), reorderLevel: normNum(p.reorderLevel),
        });
      }
      if (page >= (res.meta?.totalPages ?? 1)) break;
      page++;
    }
    return sigs;
  }

  async function handleCsvImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const [header, ...rows] = text.split('\n').filter(l => l.trim());
    // Stripped to letters only (like Parties' import) so a header like
    // "HSN SAC" or "HSN_SAC" matches just as well as "hsnsac" — previously
    // only an exact "hsnsac"/"hsn_sac"/"hsn" spelling worked.
    const cols = header.toLowerCase().split(',').map(c => c.trim().replace(/[^a-z]/g, ''));
    const get = (row: string[], col: string) => (row[cols.indexOf(col)] ?? '').trim().replace(/^"|"$/g, '');
    setImporting(true);
    const knownSigs = await fetchAllProductSigs();
    let ok = 0, fail = 0, dup = 0;
    const dupNames: string[] = [];
    for (const line of rows) {
      const row = line.split(',');
      const name = get(row, 'name');
      if (!name) { fail++; continue; }
      const rawType = get(row, 'type');
      const type = (rawType as Product['type']) || 'product';
      const rawCategory = get(row, 'category');
      const category = (rawCategory === 'raw_material' || rawCategory === 'finished_good') ? rawCategory : undefined;
      const hsnSac = get(row, 'hsnsac') || get(row, 'hsn');
      const rawUnit = get(row, 'unit');
      const unit = rawUnit || 'pcs';
      const rawRate = get(row, 'rate');
      const rate = parseFloat(rawRate) || 0;
      const rawGstRate = get(row, 'gstrate') || get(row, 'gst');
      const gstRate = parseFloat(rawGstRate) || 18;
      const rawStock = get(row, 'stock');
      const stock = parseFloat(rawStock) || 0;
      const rawOpeningStockRate = get(row, 'openingstockrate');
      const openingStockRate = rawOpeningStockRate ? parseFloat(rawOpeningStockRate) || undefined : undefined;
      const rawReorderLevel = get(row, 'reorderlevel');
      const reorderLevel = parseFloat(rawReorderLevel) || 0;
      // Matched against the raw (pre-default) values — a blank cell should
      // never force-match every other product that also happens to sit at
      // the same default (e.g. gstRate 18, stock 0).
      const rowSig: Partial<ProductSig> = {
        name: normText(name), type: normText(rawType), category: normText(rawCategory), hsnSac: normText(hsnSac), unit: normText(rawUnit),
        rate: normNum(rawRate), gstRate: normNum(rawGstRate), stock: normNum(rawStock), reorderLevel: normNum(rawReorderLevel),
      };
      if (knownSigs.some(sig => isDuplicateSig(rowSig, sig))) { dup++; dupNames.push(name); continue; }
      try {
        // openingStockQty mirrors stock, not a separate CSV column — a
        // freshly-imported product has no purchase history of its own, so
        // its opening basis (what the Stock Movement Report/FIFO costing
        // read) *is* whatever quantity this row declares. Without this, the
        // product shows a real count here but Opening: 0 in every stock
        // report — see CLAUDE.md's Products CSV note.
        await productsApi.create({ name, type, category, hsnSac: hsnSac || undefined, unit, rate, gstRate, stock, openingStockQty: stock, openingStockRate, reorderLevel });
        ok++;
        knownSigs.push({ name: normText(name), type: normText(type), category: normText(category), hsnSac: normText(hsnSac), unit: normText(unit), rate: normNum(rate), gstRate: normNum(gstRate), stock: normNum(stock), reorderLevel: normNum(reorderLevel) });
      } catch { fail++; }
    }
    setImporting(false);
    qc.invalidateQueries({ queryKey: ['products'] });
    qc.refetchQueries({ type: 'all' });
    setImportResult({ ok, dup, fail, dupNames });
    if (e.target) e.target.value = '';
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Products & Services</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input ref={csvRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCsvImport} />
          <button className="btn btn-ghost" onClick={handleExportCsv} disabled={exporting}>
            {exporting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Exporting…</> : '⬇ Export CSV'}
          </button>
          <button className="btn btn-ghost" onClick={() => setCsvInfoModal(true)} disabled={importing}>
            {importing ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Importing…</> : '⬆ Import CSV'}
          </button>
          <button className="btn btn-primary" onClick={() => { setForm(empty()); setError(null); setModal('create'); }}>+ Add Product</button>
          {gapCount > 0 && (
            <button
              className="btn btn-ghost"
              style={{ color: 'var(--warning)', borderColor: 'var(--warning)' }}
              disabled={backfill.isPending}
              title="These products show a real stock count here but Opening: 0 in the Stock Movement Report, since it reads a different, never-filled-in field"
              onClick={async () => {
                if (!(await confirm({
                  title: 'Backfill Opening Stock?',
                  message: `Sets Opening Stock = current stock for ${gapCount} product${gapCount === 1 ? '' : 's'} with no opening stock on record, so the Stock Movement Report and FIFO valuation match what this page already shows. Their selling price is used as a starting cost estimate — correct it later per product via Edit if you know the real purchase cost. This never touches a product that already has an opening stock value.`,
                  confirmLabel: `Backfill ${gapCount}`,
                }))) return;
                backfill.mutate();
              }}
            >
              {backfill.isPending ? <span className="spinner" style={{ width: 14, height: 14 }} /> : `⚠️ Backfill Opening Stock (${gapCount})`}
            </button>
          )}
          {canDelete && productsMeta && productsMeta.total > 0 && (
            <>
              {/* Visual separator — "Delete All" wipes the whole catalog, not
                  a peer action of the routine buttons to its left, so it gets
                  its own outline style + a divider instead of lining up as a
                  fifth equal-weight button. */}
              <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 0.25rem' }} />
              <button
                className="btn btn-danger-outline"
                disabled={delAll.isPending}
                onClick={async () => {
                  const count = productsMeta.total;
                  if (!(await confirm({
                    title: 'Delete ALL products & services?',
                    message: `This removes all ${count} product${count === 1 ? '' : 's'}/service${count === 1 ? '' : 's'} in this account from every list, report, and dropdown. This cannot be undone from the UI.`,
                    confirmLabel: `Delete all ${count}`,
                    danger: true,
                  }))) return;
                  // A second, explicit confirm for an action this destructive —
                  // one misclick shouldn't be able to wipe an entire product
                  // catalog; ConfirmDialog has no type-to-confirm input, so
                  // this is the cheapest meaningful extra friction available.
                  if (!(await confirm({
                    title: 'Are you absolutely sure?',
                    message: `Confirming again will permanently remove all ${count} products/services right now.`,
                    confirmLabel: 'Yes, delete everything',
                    danger: true,
                  }))) return;
                  delAll.mutate();
                }}
              >
                {delAll.isPending ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '🗑 Delete All'}
              </button>
            </>
          )}
        </div>
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or HSN/SAC…" style={{ maxWidth: 340 }} />
      </div>

      {selected.size > 0 && (
        <div className="card" style={{ padding: '0.85rem 1.25rem', marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: '0.85rem' }}>{selected.size} selected</strong>
          <button
            className="btn btn-danger btn-sm"
            disabled={bulkDel.isPending}
            onClick={async () => {
              const count = selected.size;
              if (!(await confirm({ message: `Delete ${count} selected product${count === 1 ? '' : 's'}? This cannot be undone from the UI.`, danger: true, confirmLabel: `Delete ${count}` }))) return;
              bulkDel.mutate(Array.from(selected));
            }}
          >
            {bulkDel.isPending ? <span className="spinner" style={{ width: 12, height: 12 }} /> : `Delete Selected (${selected.size})`}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>Clear selection</button>
        </div>
      )}

      <div className="card">
        {isLoading ? <div className="loading-overlay"><span className="spinner" /></div>
          : filtered.length === 0 ? <div className="empty-state"><div className="empty-state-icon">📦</div><div className="empty-state-text">No products yet</div></div>
          : (
            <table>
              <thead><tr>
                {canDelete && (
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox" style={{ width: 'auto', cursor: 'pointer' }}
                      checked={filtered.length > 0 && filtered.every(p => selected.has(p.id))}
                      onChange={e => setSelected(e.target.checked ? new Set(filtered.map(p => p.id)) : new Set())}
                    />
                  </th>
                )}
                <th>Name</th><th>Type</th><th>Category</th><th>HSN/SAC</th><th>Unit</th><th style={{ textAlign: 'right' }}>Rate</th><th style={{ textAlign: 'right' }}>GST %</th><th style={{ textAlign: 'right' }}>Stock</th><th style={{ width: 120 }}>Actions</th></tr></thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} style={{ background: selected.has(p.id) ? 'var(--primary-light)' : undefined }}>
                    {canDelete && (
                      <td>
                        <input
                          type="checkbox" style={{ width: 'auto', cursor: 'pointer' }}
                          checked={selected.has(p.id)}
                          onChange={e => setSelected(prev => { const s = new Set(prev); e.target.checked ? s.add(p.id) : s.delete(p.id); return s; })}
                        />
                      </td>
                    )}
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td><span className="badge badge-gray" style={{ textTransform: 'capitalize' }}>{p.type}</span></td>
                    <td>{p.category
                      ? <span className={`badge ${p.category === 'raw_material' ? 'badge-orange' : 'badge-blue'}`}>{p.category === 'raw_material' ? 'Raw Material' : 'Finished Good'}</span>
                      : <span style={{ color: 'var(--text-2)' }}>—</span>}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{p.hsnSac ?? '—'}</td>
                    <td>{p.unit}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(p.rate)}</td>
                    <td style={{ textAlign: 'right' }}>{p.gstRate}%</td>
                    <td style={{ textAlign: 'right', color: (p.stock ?? 0) <= (p.reorderLevel ?? 0) ? 'var(--danger)' : undefined }}>{p.stock ?? 0}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        {p.type === 'product' && <button className="btn btn-ghost btn-sm" onClick={() => { setAdjustProduct(p); setAdjustForm({ qtyChange: 0, reason: 'physical_count', notes: '' }); setAdjustError(null); }}>Stock</button>}
                        <button className="btn btn-ghost btn-sm" onClick={() => { setForm({ ...p }); setError(null); setModal(p); }}>Edit</button>
                        {canDelete && <button className="btn btn-danger btn-sm" disabled={del.isPending} onClick={async () => { if (await confirm({ message: `Delete ${p.name}?`, danger: true })) del.mutate(p.id); }}>Del</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        {productsMeta && productsMeta.totalPages > 1 && (
          <Pagination page={page} totalPages={productsMeta.totalPages} onChange={setPage} />
        )}
      </div>

      {/* ── Import CSV — column info + sample file, shown before the file picker ── */}
      {csvInfoModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setCsvInfoModal(false)}>
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <span className="modal-title">Import Products from CSV</span>
              <button className="modal-close" onClick={() => setCsvInfoModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.85rem', color: 'var(--text-2)', margin: '0 0 1.1rem' }}>
                Your file needs a header row. Only <strong>name</strong> is required — everything else is optional and can be filled in later.
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginBottom: '1.1rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', padding: '0 0.6rem 0.5rem', borderBottom: '1px solid var(--border)' }}>Column</th>
                    <th style={{ textAlign: 'left', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', padding: '0 0.6rem 0.5rem', borderBottom: '1px solid var(--border)' }}>Required?</th>
                    <th style={{ textAlign: 'left', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', padding: '0 0.6rem 0.5rem', borderBottom: '1px solid var(--border)' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {CSV_IMPORT_COLUMNS.map(c => (
                    <tr key={c.key}>
                      <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--border)', fontWeight: 700, fontFamily: 'ui-monospace, SF Mono, Menlo, monospace', fontSize: '0.8rem' }}>{c.key}</td>
                      <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--border)' }}>
                        {c.required
                          ? <span style={{ display: 'inline-flex', padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, background: 'var(--warning-light, #fffbeb)', color: 'var(--warning)' }}>Required</span>
                          : <span style={{ display: 'inline-flex', padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, background: 'var(--surface-alt)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Optional</span>}
                      </td>
                      <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '0.79rem' }}>{c.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', background: 'var(--primary-light)', border: '1px dashed var(--primary)', borderRadius: 8, padding: '0.75rem 1rem' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>Not sure where to start?</div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>Download a filled-in example with these exact columns.</div>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ flexShrink: 0, borderColor: 'var(--primary)', color: 'var(--primary)' }}
                  onClick={() => downloadCsv(SAMPLE_PRODUCTS_CSV, 'sample-products.csv')}
                >⬇ Sample CSV</button>
              </div>
            </div>
            <div className="modal-footer">
              <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>Rows that exactly match an existing product are skipped, not duplicated.</span>
              <div style={{ display: 'flex', gap: '0.6rem', flexShrink: 0 }}>
                <button className="btn btn-ghost" onClick={() => setCsvInfoModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={() => { setCsvInfoModal(false); csvRef.current?.click(); }}>Choose File</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Import results ─────────────────────────────────────────────────── */}
      {importResult && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setImportResult(null)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <span className="modal-title">Import Complete</span>
              <button className="modal-close" onClick={() => setImportResult(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: importResult.dupNames.length > 0 ? '1rem' : 0 }}>
                <div style={{ flex: 1, textAlign: 'center', background: 'var(--success-light, #f0fdf4)', borderRadius: 8, padding: '0.75rem' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--success)' }}>{importResult.ok}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>Imported</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-2)' }}>{importResult.dup}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>Duplicate, skipped</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', background: importResult.fail > 0 ? 'var(--warning-light, #fffbeb)' : 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: importResult.fail > 0 ? 'var(--warning)' : 'var(--text-2)' }}>{importResult.fail}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>Failed</div>
                </div>
              </div>
              {importResult.dupNames.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>Skipped as duplicates of an existing product:</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', maxHeight: 140, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6, padding: '0.5rem 0.75rem' }}>
                    {importResult.dupNames.join(', ')}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={() => setImportResult(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Adjustment Modal */}
      {adjustProduct && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAdjustProduct(null)}>
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <span className="modal-title">Adjust Stock — {adjustProduct.name}</span>
              <button className="modal-close" onClick={() => setAdjustProduct(null)}>×</button>
            </div>
            <div className="modal-body">
              {adjustError && <div className="error-box">{adjustError}</div>}
              <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-2)', fontSize: '0.875rem' }}>Current Stock</span>
                <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{adjustProduct.stock ?? 0} {adjustProduct.unit}</span>
              </div>
              <div className="form-row form-row-2">
                <label>Qty Change *
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem' }}>
                    <button type="button" onClick={() => setAdjustForm(f => ({ ...f, qtyChange: Math.abs(f.qtyChange) * -1 }))}
                      style={{ padding: '0.4rem 0.75rem', borderRadius: 6, border: `2px solid ${adjustForm.qtyChange < 0 ? 'var(--danger)' : '#e2e8f0'}`, background: adjustForm.qtyChange < 0 ? '#fef2f2' : 'white', cursor: 'pointer', fontWeight: 700 }}>
                      − Out
                    </button>
                    <button type="button" onClick={() => setAdjustForm(f => ({ ...f, qtyChange: Math.abs(f.qtyChange) }))}
                      style={{ padding: '0.4rem 0.75rem', borderRadius: 6, border: `2px solid ${adjustForm.qtyChange >= 0 ? 'var(--success)' : '#e2e8f0'}`, background: adjustForm.qtyChange >= 0 ? '#f0fdf4' : 'white', cursor: 'pointer', fontWeight: 700 }}>
                      + In
                    </button>
                  </div>
                  <input type="number" min={0} value={Math.abs(adjustForm.qtyChange) || ''}
                    onChange={e => setAdjustForm(f => ({ ...f, qtyChange: (parseFloat(e.target.value) || 0) * (f.qtyChange < 0 ? -1 : 1) }))}
                    placeholder="0" style={{ marginTop: '0.35rem' }} />
                </label>
                <label>Reason
                  <select value={adjustForm.reason} onChange={e => setAdjustForm(f => ({ ...f, reason: e.target.value }))} style={{ marginTop: '0.35rem' }}>
                    {ADJUST_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </label>
              </div>
              <label style={{ fontWeight: 500, fontSize: '0.875rem', display: 'block', marginTop: '0.5rem' }}>Notes
                <textarea value={adjustForm.notes} onChange={e => setAdjustForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Optional note…" style={{ width: '100%', marginTop: '0.35rem', resize: 'vertical' }} />
              </label>
              {adjustForm.qtyChange !== 0 && (
                <div style={{ marginTop: '0.75rem', background: 'var(--surface-alt)', borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}>
                  New stock: <strong>{(adjustProduct.stock ?? 0) + adjustForm.qtyChange} {adjustProduct.unit}</strong>
                  {((adjustProduct.stock ?? 0) + adjustForm.qtyChange) < (adjustProduct.reorderLevel ?? 0) && (
                    <span style={{ color: 'var(--danger)', marginLeft: '0.5rem', fontWeight: 600 }}>⚠ Below reorder level</span>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setAdjustProduct(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdjustStock} disabled={adjustSaving || adjustForm.qtyChange === 0}>
                {adjustSaving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Apply Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal" style={{ maxWidth: 620 }}>
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'Add Product / Service' : 'Edit Product'}</span>
              <button className="modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="error-box">{error}</div>}
              <div className="form-row form-row-2">
                <label>Name *<input value={form.name ?? ''} onChange={set('name')} placeholder="Product name" autoFocus /></label>
                <label>Type<select value={form.type ?? 'product'} onChange={set('type')}><option value="product">Product</option><option value="service">Service</option></select></label>
              </div>
              <div className="form-row form-row-3">
                <label>HSN / SAC
                  <div style={{ position: 'relative' }}>
                    <input value={form.hsnSac ?? ''} onChange={e => { set('hsnSac')(e); setHsnSearch(e.target.value); setShowHsnDD(true); }}
                      onFocus={e => { setHsnSearch(e.target.value); setShowHsnDD(true); }}
                      onBlur={() => setTimeout(() => setShowHsnDD(false), 150)}
                      placeholder="Type code or name…" />
                    {showHsnDD && hsnResults.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
                        {hsnResults.map(h => (
                          <div key={h.code} onMouseDown={() => {
                            setForm(f => ({ ...f, hsnSac: h.code, ...(h.defaultGstRate !== undefined ? { gstRate: h.defaultGstRate } : {}) }));
                            setHsnSearch(h.code); setShowHsnDD(false);
                          }} style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid var(--border)', display: 'flex', gap: '0.5rem', alignItems: 'center' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-alt)')} onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface)')}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.82rem', color: 'var(--primary)', minWidth: 72 }}>{h.code}</span>
                            <span style={{ fontSize: '0.78rem', color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{h.description}</span>
                            {h.defaultGstRate !== undefined && <span className="badge badge-blue" style={{ flexShrink: 0, fontSize: '0.7rem' }}>{h.defaultGstRate}%</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </label>
                <label>Unit<input value={form.unit ?? ''} onChange={set('unit')} placeholder="pcs / kg / hr" /></label>
                <label>Rate (₹)<input type="number" value={form.rate ?? ''} onChange={set('rate')} placeholder="0.00" min={0} /></label>
              </div>
              {editingProductId && form.type === 'product' && fifoCostResult && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginTop: '-0.5rem', marginBottom: '0.75rem' }}>
                  Current FIFO Cost: <strong>{fmt(fifoCostResult.fifoCost)}</strong> (from purchase history — read-only, distinct from the selling Rate above)
                </div>
              )}
              <div className="form-row form-row-3">
                <label>GST Rate<select value={form.gstRate ?? 18} onChange={set('gstRate')}>{GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}</select></label>
                <label>Stock (qty)<input type="number" value={form.stock ?? ''} onChange={set('stock')} placeholder="0" min={0} /></label>
                <label>Reorder Level<input type="number" value={form.reorderLevel ?? ''} onChange={set('reorderLevel')} placeholder="0" min={0} /></label>
              </div>
              {form.type === 'product' && (
                <div className="form-row form-row-3">
                  <label>Category<select value={form.category ?? ''} onChange={set('category')}>
                    <option value="">— Not categorized —</option>
                    <option value="raw_material">Raw Material</option>
                    <option value="finished_good">Finished Good</option>
                  </select></label>
                  <label>Opening Stock Qty<input type="number" min={0} step={0.001} value={form.openingStockQty ?? ''} onChange={set('openingStockQty')} placeholder="0" /></label>
                  <label>Opening Stock Rate (₹)<input type="number" min={0} step={0.01} value={form.openingStockRate ?? ''} onChange={e => {
                    const v = parseFloat(e.target.value) || 0;
                    // Rate (₹) drives billing/BOM costing and is easy to leave blank while filling
                    // this field instead — default it from here too, but only if untouched, so we
                    // never clobber a deliberately-set Rate (including an explicit 0).
                    setForm(f => ({ ...f, openingStockRate: v, ...(f.rate === undefined ? { rate: v } : {}) }));
                  }} placeholder="0.00" /></label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
